/**
 * The feedback page's viewer (#660, #661): the white page on the warm ground with a note gutter beside it, a floating toolbar over its
 * bottom, and the page thumbnails below. Page, frames, connectors and notes are one layer and move and scale together.
 *
 * - **The stage is a CSS transform on the page element** (`translate` then `scale`, from the page's centred "Fit" place), driven by
 *   pointer events. The mouse wheel zooms at the pointer, a drag pans, a double click toggles Fit; on touch one finger pans and two
 *   pinch; the toolbar's zoom out, Fit and zoom in keep working; with the stage focused the arrow keys pan and `+`, `-` and `0` zoom
 *   and fit. The page cannot be lost: the view is clamped so the page always covers the stage, or sits centred when it is smaller.
 * - **Nothing here asks for or records anything.** It is presentation over whatever the caller draws inside (`children`): the page
 *   image with its frames and pins, which live in the page's own coordinate system and so move and scale with it. A pin is kept
 *   44px on screen at any zoom by the stylesheet, and a press that starts on a pin never starts a drag, so a click still follows it.
 * - `touch-action: none` is set on the stage only. The hint ("Drag to move...") fades after the first interaction; nothing moves
 *   by itself, so there is no motion to switch off.
 */

import { useCallback, useEffect, useRef, useState } from 'react';
import { useMessages } from '../../i18n/locale.js';
import { feedbackChrome } from './feedback-chrome.js';

const MAX_ZOOM = 4;
const ZOOM_STEPS = [1, 1.5, 2, 3, 4] as const;
const KEY_PAN_PX = 48;
const EPSILON = 0.001;

interface View {
  /** 1 is Fit: the whole page inside the stage. */
  readonly z: number;
  /** Offset of the page's centre from the stage's centre, in screen pixels. */
  readonly x: number;
  readonly y: number;
}
const FIT: View = { z: 1, x: 0, y: 0 };

interface Size {
  readonly w: number;
  readonly h: number;
}

const between = (value: number, low: number, high: number): number => Math.min(high, Math.max(low, value));

/** The view kept inside the stage: a page larger than the stage never shows past its edge; a smaller one stays centred. */
export function clampView(view: View, stage: Size, sheet: Size): View {
  const z = between(view.z, 1, MAX_ZOOM);
  const limitX = Math.max(0, (sheet.w * z - stage.w) / 2);
  const limitY = Math.max(0, (sheet.h * z - stage.h) / 2);
  return { z, x: between(view.x, -limitX, limitX), y: between(view.y, -limitY, limitY) };
}

/** Zoom to `z`, keeping the page point under (px, py) - relative to the stage's centre - where it is. */
function zoomAround(view: View, z: number, px: number, py: number): View {
  const next = between(z, 1, MAX_ZOOM);
  const k = next / view.z;
  return { z: next, x: px - (px - view.x) * k, y: py - (py - view.y) * k };
}

interface Viewport {
  readonly stageRef: React.RefObject<HTMLDivElement | null>;
  readonly sheetRef: React.RefObject<HTMLDivElement | null>;
  readonly view: View;
  readonly touched: boolean;
  readonly dragging: boolean;
  readonly handlers: React.HTMLAttributes<HTMLDivElement>;
  readonly zoomIn: () => void;
  readonly zoomOut: () => void;
  readonly fit: () => void;
}

/** A press that moves less than this, in screen pixels, is a tap, not the start of a drag. */
const TAP_SLOP_PX = 5;

/** Anything that must take a click of its own, and so never starts a drag. */
const HIT = '[data-hit]';

export function useViewport(onTap?: (id: string) => void): Viewport {
  const stageRef = useRef<HTMLDivElement | null>(null);
  const tap = useRef<{ x: number; y: number; moved: boolean } | null>(null);
  const onTapRef = useRef(onTap);
  onTapRef.current = onTap;
  const sheetRef = useRef<HTMLDivElement | null>(null);
  const [view, setView] = useState<View>(FIT);
  const viewRef = useRef<View>(FIT);
  const [touched, setTouched] = useState(false);
  const [dragging, setDragging] = useState(false);
  const pointers = useRef(new Map<number, { x: number; y: number }>());
  const pinch = useRef<{ distance: number; cx: number; cy: number } | null>(null);

  const metrics = useCallback((): { stage: Size; sheet: Size; cx: number; cy: number } => {
    const stage = stageRef.current;
    const sheet = sheetRef.current;
    const rect = stage?.getBoundingClientRect();
    return {
      stage: { w: stage?.clientWidth ?? 0, h: stage?.clientHeight ?? 0 },
      sheet: { w: sheet?.offsetWidth ?? 0, h: sheet?.offsetHeight ?? 0 },
      cx: (rect?.left ?? 0) + (rect?.width ?? 0) / 2,
      cy: (rect?.top ?? 0) + (rect?.height ?? 0) / 2,
    };
  }, []);

  const change = useCallback(
    (update: (current: View) => View): void => {
      const m = metrics();
      const next = clampView(update(viewRef.current), m.stage, m.sheet);
      viewRef.current = next;
      setView(next);
    },
    [metrics],
  );

  // The wheel needs a non-passive listener to keep the page from scrolling under the pointer, which React's `onWheel` cannot give.
  useEffect(() => {
    const stage = stageRef.current;
    if (stage === null) return undefined;
    const onWheel = (event: WheelEvent): void => {
      event.preventDefault();
      const m = metrics();
      const factor = Math.exp(-event.deltaY * (event.deltaMode === 1 ? 0.05 : 0.0015));
      change((current) => zoomAround(current, current.z * factor, event.clientX - m.cx, event.clientY - m.cy));
      setTouched(true);
    };
    stage.addEventListener('wheel', onWheel, { passive: false });
    return () => stage.removeEventListener('wheel', onWheel);
  }, [metrics, change]);

  useEffect(() => {
    const onResize = (): void => change((current) => current);
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, [change]);

  const onPointerDown = (event: React.PointerEvent<HTMLDivElement>): void => {
    if (event.pointerType === 'mouse' && event.button !== 0) return;
    // A press on a pin or a note is a click, never the start of a drag.
    if ((event.target as Element).closest(HIT) !== null) return;
    tap.current = pointers.current.size === 0 ? { x: event.clientX, y: event.clientY, moved: false } : null;
    try {
      event.currentTarget.setPointerCapture(event.pointerId);
    } catch {
      // Not every environment can capture; the drag still works while the pointer stays over the stage.
    }
    pointers.current.set(event.pointerId, { x: event.clientX, y: event.clientY });
    if (pointers.current.size === 2) {
      const [a, b] = [...pointers.current.values()] as [{ x: number; y: number }, { x: number; y: number }];
      pinch.current = { distance: Math.hypot(a.x - b.x, a.y - b.y), cx: (a.x + b.x) / 2, cy: (a.y + b.y) / 2 };
    }
    setDragging(true);
    setTouched(true);
  };

  const onPointerMove = (event: React.PointerEvent<HTMLDivElement>): void => {
    const last = pointers.current.get(event.pointerId);
    if (last === undefined) return;
    pointers.current.set(event.pointerId, { x: event.clientX, y: event.clientY });
    if (tap.current !== null && Math.hypot(event.clientX - tap.current.x, event.clientY - tap.current.y) > TAP_SLOP_PX) tap.current.moved = true;
    if (pointers.current.size === 1) {
      const dx = event.clientX - last.x;
      const dy = event.clientY - last.y;
      change((current) => ({ ...current, x: current.x + dx, y: current.y + dy }));
      return;
    }
    const before = pinch.current;
    if (before === null || pointers.current.size !== 2) return;
    const [a, b] = [...pointers.current.values()] as [{ x: number; y: number }, { x: number; y: number }];
    const distance = Math.hypot(a.x - b.x, a.y - b.y);
    const cx = (a.x + b.x) / 2;
    const cy = (a.y + b.y) / 2;
    const m = metrics();
    if (before.distance > 0) {
      const ratio = distance / before.distance;
      change((current) => {
        const zoomed = zoomAround(current, current.z * ratio, cx - m.cx, cy - m.cy);
        return { ...zoomed, x: zoomed.x + (cx - before.cx), y: zoomed.y + (cy - before.cy) };
      });
    }
    pinch.current = { distance, cx, cy };
  };

  const onPointerEnd = (event: React.PointerEvent<HTMLDivElement>): void => {
    // A tap on a frame selects its note. The stage holds the pointer, so the element under it is looked up, not read off the event.
    if (event.type === 'pointerup' && tap.current !== null && !tap.current.moved && onTapRef.current !== undefined && typeof document.elementFromPoint === 'function') {
      const id = document.elementFromPoint(event.clientX, event.clientY)?.closest<HTMLElement>('[data-frame-id]')?.dataset['frameId'];
      if (id !== undefined) onTapRef.current(id);
    }
    tap.current = null;
    pointers.current.delete(event.pointerId);
    if (pointers.current.size < 2) pinch.current = null;
    if (pointers.current.size === 0) setDragging(false);
  };

  const onDoubleClick = (event: React.MouseEvent<HTMLDivElement>): void => {
    if ((event.target as Element).closest(HIT) !== null) return;
    const m = metrics();
    setTouched(true);
    change((current) => (current.z > 1 + EPSILON ? FIT : zoomAround(current, 2, event.clientX - m.cx, event.clientY - m.cy)));
  };

  const onKeyDown = (event: React.KeyboardEvent<HTMLDivElement>): void => {
    if (event.target !== event.currentTarget || event.altKey || event.ctrlKey || event.metaKey) return;
    const pan: Record<string, readonly [number, number]> = {
      ArrowLeft: [KEY_PAN_PX, 0],
      ArrowRight: [-KEY_PAN_PX, 0],
      ArrowUp: [0, KEY_PAN_PX],
      ArrowDown: [0, -KEY_PAN_PX],
    };
    const move = pan[event.key];
    if (move !== undefined) change((current) => ({ ...current, x: current.x + move[0], y: current.y + move[1] }));
    else if (event.key === '+' || event.key === '=') change((current) => zoomAround(current, nextStep(current.z, 1), 0, 0));
    else if (event.key === '-') change((current) => zoomAround(current, nextStep(current.z, -1), 0, 0));
    else if (event.key === '0') change(() => FIT);
    else return;
    event.preventDefault();
    setTouched(true);
  };

  return {
    stageRef,
    sheetRef,
    view,
    touched,
    dragging,
    handlers: { onPointerDown, onPointerMove, onPointerUp: onPointerEnd, onPointerCancel: onPointerEnd, onDoubleClick, onKeyDown },
    zoomIn: () => {
      setTouched(true);
      change((current) => zoomAround(current, nextStep(current.z, 1), 0, 0));
    },
    zoomOut: () => {
      setTouched(true);
      change((current) => zoomAround(current, nextStep(current.z, -1), 0, 0));
    },
    fit: () => {
      setTouched(true);
      change(() => FIT);
    },
  };
}

/** The next toolbar zoom step above (`1`) or below (`-1`) the current zoom. */
function nextStep(z: number, direction: 1 | -1): number {
  if (direction === 1) return ZOOM_STEPS.find((step) => step > z + EPSILON) ?? MAX_ZOOM;
  return [...ZOOM_STEPS].reverse().find((step) => step < z - EPSILON) ?? 1;
}


export interface ViewerThumb {
  readonly id: string;
  readonly number: number;
}

/** The page's own box on screen, in layout pixels (a zoom is a transform, so it is not part of this). */
export interface PageBox {
  readonly w: number;
  readonly h: number;
}

/** The width and height of an element, kept current. Zero until it has a layout (and always in a test). */
export function useBox(ref: React.RefObject<HTMLElement | null>): PageBox {
  const [box, setBox] = useState<PageBox>({ w: 0, h: 0 });
  useEffect(() => {
    const el = ref.current;
    if (el === null) return undefined;
    const read = (): void => setBox((now) => (now.w === el.offsetWidth && now.h === el.offsetHeight ? now : { w: el.offsetWidth, h: el.offsetHeight }));
    read();
    if (typeof ResizeObserver === 'undefined') return undefined;
    const observer = new ResizeObserver(read);
    observer.observe(el);
    return () => observer.disconnect();
  }, [ref]);
  return box;
}

export interface PageViewerProps {
  /** The toolbar and the thumbnails are drawn only on the page being shown; a hidden page keeps its stage for its image. */
  readonly current: boolean;
  readonly pageId: string;
  readonly pageNumber: number;
  readonly totalPages: number;
  readonly thumbs: readonly ViewerThumb[];
  readonly previousId: string | undefined;
  readonly nextId: string | undefined;
  readonly onGo: (pageId: string) => void;
  readonly widthPx: number;
  readonly heightPx: number;
  /** The page image with whatever is drawn on it. */
  readonly children: React.ReactNode;
  /** The note gutter beside the page, inside the zoomable layer. It is given the page's box on screen, to place notes by. */
  readonly gutter?: (box: PageBox) => React.ReactNode;
  /** A tap, without a drag, on an element carrying `data-frame-id`. */
  readonly onTap?: (frameId: string) => void;
}

export function PageViewer(props: PageViewerProps): React.JSX.Element {
  const m = useMessages(feedbackChrome);
  const viewport = useViewport(props.onTap);
  const pageRef = useRef<HTMLDivElement | null>(null);
  const box = useBox(pageRef);
  const { view } = viewport;
  const reset = viewport.fit;
  // A page that is stepped away from comes back at Fit.
  useEffect(() => {
    if (!props.current) reset();
    // eslint-disable-next-line react-hooks/exhaustive-deps -- only the page leaving the screen resets
  }, [props.current]);
  const atFit = view.z <= 1 + EPSILON;
  return (
    <div className="fb-viewer" role="group" aria-label={m.viewer}>
      <div className="fb-viewer-body">
        <div
          ref={viewport.stageRef}
          className="fb-stage"
          // Focusable so the arrow keys can pan it; named for what it is.
          tabIndex={0}
          role="group"
          aria-label={m.stageLabel(props.pageNumber)}
          data-dragging={viewport.dragging}
          data-touched={viewport.touched}
          data-gutter={props.gutter !== undefined}
          style={{ ['--fb-w' as string]: props.widthPx, ['--fb-h' as string]: props.heightPx }}
          {...viewport.handlers}
        >
          <div
            ref={viewport.sheetRef}
            className="fb-sheet"
            style={{ transform: `translate(${String(view.x)}px, ${String(view.y)}px) scale(${String(view.z)})`, ['--fb-zoom' as string]: view.z }}
          >
            <div ref={pageRef} className="fb-sheet-page" style={{ aspectRatio: `${String(props.widthPx)} / ${String(props.heightPx)}` }}>
              {props.children}
            </div>
            {props.gutter !== undefined ? <div className="fb-gutter" role="group" aria-label={m.noteGutter}>{props.gutter(box)}</div> : null}
          </div>
          {props.current ? (
            <>
              <p className="fb-hint" aria-hidden="true">
                <span className="fb-hint-desktop">{m.hintDesktop}</span>
                <span className="fb-hint-touch">{m.hintTouch}</span>
              </p>
              <div className="fb-toolbar" data-hit>
                <div className="fb-toolbar-group">
                  <button type="button" className="fb-tool" aria-label={m.previousPage} disabled={props.previousId === undefined} onClick={() => props.previousId !== undefined && props.onGo(props.previousId)}>
                    <span aria-hidden="true" className="fb-arrow">‹</span>
                  </button>
                  <span className="fb-toolbar-text">{m.pageOf(props.pageNumber, props.totalPages)}</span>
                  <button type="button" className="fb-tool" aria-label={m.nextPage} disabled={props.nextId === undefined} onClick={() => props.nextId !== undefined && props.onGo(props.nextId)}>
                    <span aria-hidden="true" className="fb-arrow">›</span>
                  </button>
                </div>
                <span aria-hidden="true" className="fb-toolbar-rule" />
                <div className="fb-toolbar-group">
                  <button type="button" className="fb-tool" aria-label={m.zoomOut} disabled={atFit} onClick={viewport.zoomOut}>
                    <span aria-hidden="true">−</span>
                  </button>
                  <button type="button" className="fb-fit" disabled={atFit} onClick={viewport.fit}>
                    {m.fit}
                  </button>
                  <button type="button" className="fb-tool" aria-label={m.zoomIn} disabled={view.z >= MAX_ZOOM - EPSILON} onClick={viewport.zoomIn}>
                    <span aria-hidden="true">+</span>
                  </button>
                </div>
              </div>
            </>
          ) : null}
        </div>
      </div>
      {props.current ? (
        <div className="fb-thumbs" role="group" aria-label={m.pagesNav}>
          {props.thumbs.map((thumb) => (
            <button
              key={thumb.id}
              type="button"
              className="fb-thumb"
              aria-label={m.thumb(thumb.number, thumb.id === props.pageId)}
              aria-current={thumb.id === props.pageId ? 'true' : undefined}
              onClick={() => props.onGo(thumb.id)}
            >
              {thumb.number}
            </button>
          ))}
        </div>
      ) : null}
    </div>
  );
}
